import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault } from 'jotai/utils';
import { ReconConfigDataModel } from '../../../schemas/ReconConfig.schema';
import { SecurityReport } from '../../../schemas/SecurityReport.schema';
import { TransactionAudit } from '../../../types';
import { IRoundReconConfigDataModel } from '../../../data-models/finance-round-recon.data-model';
import {
  fetchMetricsRecon,
  fetchReconConfig,
  fetchRoundReconConfig,
  fetchTransactionHistory,
} from '../../../services/queries/MaggieTransactionQueries';
import { getSecurityReport } from '../../../services/queries/MaggieMetricsQueries';

/**
 * Transaction reconciliation data atom.
 * Fetches the metrics reconciliation data, including SOI, on first read.
 * `set(atom, RESET)` re-fetches; setting a value overrides it, which is how the frosted
 * sample supplies its own rows without reaching the backend.
 */
export const transactionReconAtom = atomWithDefault(async () => {
  // Fetch the latest data from the backend including SOI data
  return fetchMetricsRecon(true);
});

/**
 * Recon configuration atom. Same contract as {@link transactionReconAtom}:
 * fetches on first read, `RESET` re-fetches, a set value wins.
 */
export const reconConfigAtom = atomWithDefault(async () => {
  // Fetch the latest data from the backend
  return fetchReconConfig();
});

/**
 * Derived atom that converts recon config array to a Map for efficient lookups
 */
export const reconConfigMapAtom = atom<Promise<Map<number, ReconConfigDataModel>>>(async (get) => {
  const reconConfig = await get(reconConfigAtom);
  const map = new Map<number, ReconConfigDataModel>();
  reconConfig.forEach((r) => map.set(r.id, r));
  return map;
});

export const roundReconConfigState = atomWithDefault<
  IRoundReconConfigDataModel | Promise<IRoundReconConfigDataModel>
>(() => fetchRoundReconConfig());

export const securityReportState = atomWithDefault<SecurityReport[] | Promise<SecurityReport[]>>(() =>
  getSecurityReport()
);

export const transactionHistoryByCompanyState = atomFamily((companyId: number) =>
  atomWithDefault<TransactionAudit[] | Promise<TransactionAudit[]>>(() => fetchTransactionHistory(companyId))
);
